import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/common/DataTable';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { ShieldCheck, Users, ToggleLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { UserService } from '../services/users.service';

const COLUMNS = [
  { 
    header: 'Name', 
    accessor: 'name', 
    render: (row) => `${row.first_name} ${row.last_name}` 
  },
  { header: 'Email', accessor: 'email' },
  { 
    header: 'Role', 
    accessor: 'roleDetails',
    render: (row) => row.roleDetails?.display_name || row.roleDetails?.name || 'N/A'
  },
  { header: 'Status', accessor: 'status' },
];

export default function Admins() {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 8;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await UserService.getAllUsers();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formatted = list.map(item => ({ ...item, id: item._id || item.id }));
      setDataList(formatted);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = useMemo(() => {
    if (!debouncedQuery) return dataList;
    const lowerQuery = debouncedQuery.toLowerCase();
    return dataList.filter(item => {
      const fullName = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
      const roleName = (item.roleDetails?.name || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      return fullName.includes(lowerQuery) || email.includes(lowerQuery) || roleName.includes(lowerQuery);
    });
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    const active = dataList.filter(d => d.status === 'active').length;
    return [
      { label: 'Total Admins', value: total, icon: <Users size={20} />, color: 'blue' },
      { label: 'Active Admins', value: active, icon: <ShieldCheck size={20} />, color: 'green' },
      { label: 'Inactive Admins', value: total - active, icon: <ToggleLeft size={20} />, color: 'orange' },
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleEdit = (row) => {
    const id = row._id || row.id;
    navigate(`/admins/edit/${id}`);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await UserService.deleteUser(targetId);
      setDataList(prev => prev.filter(item => item.id !== targetId));
      toast.success('Admin deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete admin');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader 
        title="Admin Users" 
        subtitle="Manage administrative accounts, their roles and access."
        actionButton={{
          label: "Add Admin",
          onClick: () => navigate('/admins/new')
        }}
        kpis={kpis}
      />

      <div style={{ 
        background: 'white', 
        borderRadius: '16px', 
        padding: '24px', 
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Admin List</h2>
          <div style={{ width: '300px' }}>
            <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search admins..." />
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading admins...
          </div>
        ) : (
          <>
            <DataTable 
              columns={COLUMNS} 
              data={paginatedData} 
              onEdit={handleEdit} 
              onDelete={handleDeleteClick} 
            />
            {totalPages > 1 && (
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={setCurrentPage} 
              />
            )}
            {paginatedData.length === 0 && (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
                No admins found.
              </div>
            )}
          </>
        )}
      </div>

      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteExecute}
        title="Delete Admin"
        message={`Are you sure you want to delete ${deleteTarget?.first_name} ${deleteTarget?.last_name}? This action cannot be undone.`}
      />
    </div>
  );
}
